import React, { Component, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Chip,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  IconButton,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../contexts/AuthContext';
import TopBarActions from '../../components/layout/TopBarActions';
import IdentityCarousel from '../../components/IdentityCarousel';
import {
  ARCA_APRESENTACAO_HABILITADA,
  canAccessArcaProcessosContratos,
} from './utils/arcaAccess';

/**
 * Paleta alinhada ao shell SGC (App.js / Contratos):
 * drawer claro, accent azul #1976d2, fundo main #f8fafc → #e3f2fd.
 */
const PALETTE = {
  bg: {
    shell: 'linear-gradient(180deg, #f8fafc 0%, #e3f2fd 100%)',
    drawer: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.9) 100%)',
    paper: '#ffffff',
    header: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 40%, #7b1fa2 100%)',
  },
  text: {
    primary: '#0d47a1',
    secondary: '#1565c0',
    muted: '#64748b',
    subtle: '#90a4ae',
    nav: '#37474f',
    heading: '#0d47a1',
  },
  accent: {
    main: '#1976d2',
    selectedBg: 'rgba(25,118,210,0.1)',
    selectedBgStrong: 'rgba(25,118,210,0.15)',
    selectedHover: 'rgba(25,118,210,0.18)',
  },
  border: {
    light: 'rgba(25,118,210,0.12)',
    divider: 'rgba(25,118,210,0.1)',
  },
  hover: {
    navItem: 'rgba(25,118,210,0.06)',
  },
};

const DRAWER_W = 240;

const NAV_ITEMS_BASE = [
  {
    label: 'Apresentação',
    path: '/arca/apresentacao',
    icon: <SlideshowIcon />,
    description: 'Slides e modelos',
    feature: 'apresentacao',
    children: [
      {
        label: 'Workspace',
        path: '/arca/apresentacao/workspace',
        icon: <SlideshowIcon fontSize="small" />,
      },
      {
        label: 'Modelos',
        path: '/arca/apresentacao/modelos',
        icon: <FolderCopyOutlinedIcon fontSize="small" />,
      },
      {
        label: 'Configurações',
        path: '/arca/apresentacao/configuracoes',
        icon: <SettingsOutlinedIcon fontSize="small" />,
      },
    ],
  },
  {
    label: 'Formulários',
    path: '/arca/formularios',
    icon: <DescriptionOutlinedIcon />,
    description: 'Checklists e inspeções',
    children: [
      {
        label: 'Templates',
        path: '/arca/formularios',
        icon: <DescriptionOutlinedIcon fontSize="small" />,
        exact: true,
      },
      {
        label: 'Configuração',
        path: '/arca/formularios/configuracao',
        icon: <TuneOutlinedIcon fontSize="small" />,
      },
    ],
  },
  {
    label: 'Editais',
    path: '/arca/editais',
    icon: <CampaignOutlinedIcon />,
    description: 'Kanban GAV + portal',
  },
  {
    label: 'PDF',
    path: '/arca/pdf',
    icon: <PictureAsPdfOutlinedIcon />,
    description: 'Ferramentas de PDF',
    children: [
      {
        label: 'Ferramentas',
        path: '/arca/pdf',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
        exact: true,
      },
      {
        label: 'Juntar PDF',
        path: '/arca/pdf/juntar',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
      },
      {
        label: 'Dividir PDF',
        path: '/arca/pdf/dividir',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
      },
      {
        label: 'Organizar PDF',
        path: '/arca/pdf/organizar',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
      },
      {
        label: 'Comprimir PDF',
        path: '/arca/pdf/comprimir',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
      },
      {
        label: 'JPG para PDF',
        path: '/arca/pdf/jpg-para-pdf',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
      },
      {
        label: 'Word para PDF',
        path: '/arca/pdf/word-para-pdf',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
      },
      {
        label: 'PDF para JPG',
        path: '/arca/pdf/pdf-para-jpg',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
      },
      {
        label: 'PDF para Word',
        path: '/arca/pdf/pdf-para-word',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
      },
      {
        label: 'Processo pagamento',
        path: '/arca/pdf/processo-pagamento',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
        requiresProcessosContratos: true,
      },
      {
        label: 'Processo administrativo',
        path: '/arca/pdf/processo-administrativo',
        icon: <PictureAsPdfOutlinedIcon fontSize="small" />,
        requiresProcessosContratos: true,
      },
    ],
  },
];

function buildNavItems({ canProcessosContratos }) {
  return NAV_ITEMS_BASE.filter((item) => {
    if (item.feature === 'apresentacao' && !ARCA_APRESENTACAO_HABILITADA) return false;
    return true;
  }).map((item) => {
    if (!item.children) return item;
    return {
      ...item,
      children: item.children.filter((child) => {
        if (child.requiresProcessosContratos && !canProcessosContratos) return false;
        return true;
      }),
    };
  });
}

function isNavItemActive(pathname, itemPath, exact) {
  if (exact) return pathname === itemPath;
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function getArcaPageTitle(pathname, navItems) {
  for (const item of navItems) {
    if (item.children) {
      for (const child of item.children) {
        if (isNavItemActive(pathname, child.path, child.exact)) {
          return `${item.label} · ${child.label}`;
        }
      }
    }
    if (isNavItemActive(pathname, item.path)) return item.label;
  }
  return 'ARCA';
}

const navItemSx = (active, nested = false) => ({
  borderRadius: 3,
  mb: nested ? 0.25 : 0.5,
  pl: nested ? 2 : 1.5,
  color: active ? PALETTE.text.primary : PALETTE.text.nav,
  bgcolor: active ? (nested ? PALETTE.accent.selectedBgStrong : PALETTE.accent.selectedBg) : 'transparent',
  fontWeight: active ? 700 : 500,
  transition: 'all 0.2s ease',
  '&.Mui-selected': {
    bgcolor: nested ? PALETTE.accent.selectedBgStrong : PALETTE.accent.selectedBg,
    '&:hover': { bgcolor: PALETTE.accent.selectedHover },
  },
  '&:hover': {
    bgcolor: PALETTE.hover.navItem,
    transform: 'translateX(4px)',
    boxShadow: '0 4px 12px rgba(25,118,210,0.15)',
  },
});

/** Error Boundary local: falha na página interna não derruba o shell do ARCA. */
class ArcaOutletErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ArcaLayout] Erro na rota interna:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          role="alert"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            p: 4,
            m: { xs: 2, md: 3 },
            borderRadius: 3,
            border: `1px solid ${PALETTE.border.light}`,
            bgcolor: PALETTE.bg.paper,
            boxShadow: '0 4px 20px rgba(25,118,210,0.08)',
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} color={PALETTE.text.heading} gutterBottom>
            Erro ao carregar esta área
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 420, mb: 3 }}>
            Algo falhou nesta página do ARCA. O menu lateral continua disponível — tente novamente ou volte às
            ferramentas de PDF.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Tentar novamente
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.location.assign('/arca/pdf')}
              sx={{ textTransform: 'none' }}
            >
              Ir para PDF
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

function ArcaSidebar({ location, onNavigate, onCloseMobile, unidadeLabel, navItems }) {
  const [openGroups, setOpenGroups] = useState(() => ({
    '/arca/formularios': true,
    '/arca/pdf': true,
  }));

  const toggleGroup = (path) => {
    setOpenGroups((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: PALETTE.bg.drawer,
        backdropFilter: 'blur(10px)',
        color: PALETTE.text.nav,
      }}
    >
      {/* Faixa brand — mesmo idioma visual do header do SGC */}
      <Box
        sx={{
          minHeight: 88,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: PALETTE.bg.header,
          borderRadius: '0 20px 0 0',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px 0 rgba(123,31,162,0.10), 0 1.5px 8px 0 rgba(25,118,210,0.10)',
          px: 2,
          py: 2,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 60% 30%, rgba(255,255,255,0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Typography
          variant="overline"
          sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 2, fontSize: '0.65rem', zIndex: 1, lineHeight: 1 }}
        >
          Módulo
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ color: '#fff', lineHeight: 1.2, zIndex: 1 }}>
          ARCA
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', zIndex: 1, mt: 0.25 }}>
          Formulários · Editais · PDF
        </Typography>
      </Box>

      {unidadeLabel && (
        <Box sx={{ px: 2, py: 1.25 }}>
          <Chip
            label={unidadeLabel}
            size="small"
            sx={{
              width: '100%',
              fontWeight: 700,
              color: 'primary.main',
              bgcolor: 'rgba(25,118,210,0.08)',
              border: '1px solid rgba(25,118,210,0.2)',
              borderRadius: 2,
            }}
          />
        </Box>
      )}

      <Divider sx={{ borderColor: PALETTE.border.divider }} />

      <List sx={{ px: 1, py: 1.5, flex: 1, overflow: 'auto' }}>
        {navItems.map((item) => {
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const groupActive = isNavItemActive(location.pathname, item.path);
          const expanded = openGroups[item.path] ?? groupActive;

          return (
            <Box key={item.path}>
              <ListItemButton
                selected={!hasChildren && groupActive}
                onClick={() => {
                  if (hasChildren) {
                    toggleGroup(item.path);
                    if (!groupActive) {
                      onNavigate(item.children[0].path);
                      onCloseMobile();
                    }
                  } else {
                    onNavigate(item.path);
                    onCloseMobile();
                  }
                }}
                sx={navItemSx(groupActive && !hasChildren)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: groupActive ? PALETTE.accent.main : 'primary.main',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={item.description}
                  primaryTypographyProps={{
                    fontWeight: groupActive ? 700 : 600,
                    fontSize: '0.9rem',
                    color: groupActive ? PALETTE.text.primary : PALETTE.text.nav,
                  }}
                  secondaryTypographyProps={{ fontSize: '0.68rem', color: PALETTE.text.subtle }}
                />
                {hasChildren && (expanded ? <ExpandLess color="primary" /> : <ExpandMore color="action" />)}
              </ListItemButton>

              {hasChildren && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1, mb: 1, ml: 1 }}>
                    {item.children.map((child) => {
                      const active = isNavItemActive(location.pathname, child.path, child.exact);
                      return (
                        <ListItemButton
                          key={`${child.path}-${child.label}`}
                          selected={active}
                          onClick={() => {
                            onNavigate(child.path);
                            onCloseMobile();
                          }}
                          sx={navItemSx(active, true)}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              color: active ? PALETTE.accent.main : 'primary.main',
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontWeight: active ? 700 : 500,
                              fontSize: '0.82rem',
                              color: active ? PALETTE.text.primary : PALETTE.text.nav,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>

      <Divider sx={{ borderColor: PALETTE.border.divider }} />

      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={() => onNavigate('/home')}
          sx={{
            borderRadius: 3,
            color: PALETTE.text.muted,
            border: `1px solid ${PALETTE.border.light}`,
            '&:hover': {
              bgcolor: PALETTE.hover.navItem,
              color: PALETTE.accent.main,
              transform: 'translateX(4px)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <ArrowBackIcon />
          </ListItemIcon>
          <ListItemText
            primary="Voltar ao Painel"
            primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );
}

function ArcaTopBar({ title, onBack, showMenu, onOpenMenu, onRefresh }) {
  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.9) 100%)',
        backdropFilter: 'blur(10px)',
        color: '#1976d2',
        mb: 0,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        borderBottom: '1px solid rgba(255,255,255,0.3)',
        borderRadius: '0 0 20px 20px',
        flexShrink: 0,
      }}
    >
      <Toolbar sx={{ flexWrap: 'wrap', minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 }, gap: 1 }}>
        {showMenu && (
          <IconButton onClick={onOpenMenu} aria-label="Abrir menu ARCA" color="primary" sx={{ mr: 0.5 }}>
            <MenuIcon />
          </IconButton>
        )}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            textTransform: 'none',
            flexShrink: 0,
            '&:hover': { background: 'rgba(25, 118, 210, 0.1)' },
          }}
        >
          Voltar ao Painel
        </Button>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: { xs: '1rem', sm: '1.25rem' },
          }}
        >
          {title}
        </Typography>
        <TopBarActions />
        <IconButton
          onClick={onRefresh}
          aria-label="Atualizar página"
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(25,118,210,0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(25,118,210,0.4)',
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
}

export default function ArcaLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { unidadeSelecionada, user, isAdmin, isFiscal, isCargoCorporativo, rbacRole } = useAuth();
  const canProcessosContratos = useMemo(
    () =>
      canAccessArcaProcessosContratos({
        user,
        isAdmin,
        isFiscal,
        isCargoCorporativo,
        rbacRole,
      }),
    [user, isAdmin, isFiscal, isCargoCorporativo, rbacRole]
  );
  const navItems = useMemo(
    () => buildNavItems({ canProcessosContratos }),
    [canProcessosContratos]
  );
  const isWorkspace = location.pathname.includes('/apresentacao/workspace');
  const pageTitle = useMemo(
    () => getArcaPageTitle(location.pathname, navItems),
    [location.pathname, navItems]
  );
  const unidadeLabel = unidadeSelecionada?.nome || unidadeSelecionada?.codigo || '';

  const drawerPaperSx = {
    width: DRAWER_W,
    border: 0,
    boxSizing: 'border-box',
    background: PALETTE.bg.drawer,
    borderRadius: '0 20px 20px 0',
    boxShadow: '0 8px 32px rgba(25,118,210,0.12)',
  };

  const sidebar = (
    <ArcaSidebar
      location={location}
      onNavigate={navigate}
      onCloseMobile={() => setMobileOpen(false)}
      unidadeLabel={unidadeLabel}
      navItems={navItems}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        height: '100vh',
        background: PALETTE.bg.shell,
        overflow: 'hidden',
      }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_W,
            flexShrink: 0,
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
          open
        >
          {sidebar}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: { xs: 1.5, sm: 2, md: 2.5 },
          pt: { xs: 1, sm: 1.5 },
          boxSizing: 'border-box',
        }}
      >
        <ArcaTopBar
          title={pageTitle}
          onBack={() => navigate('/home')}
          showMenu={isMobile}
          onOpenMenu={() => setMobileOpen(true)}
          onRefresh={() => {
            window.dispatchEvent(new CustomEvent('refreshPage'));
          }}
        />

        {/* Mesmo chrome global do SGC: CEAP-SOL · Propósito · ISG */}
        {!isWorkspace && (
          <Box sx={{ mt: 2, flexShrink: 0 }}>
            <IdentityCarousel />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflow: isWorkspace ? 'hidden' : 'auto',
            mt: isWorkspace ? 2 : 0,
            minHeight: 0,
          }}
        >
          <ArcaOutletErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ArcaOutletErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}

export { PALETTE, NAV_ITEMS_BASE as NAV_ITEMS, buildNavItems, isNavItemActive, getArcaPageTitle };
